import * as d3 from 'd3'
import { useMemo } from 'react'

import {
  type ChartControlsState,
  ChartTooltip,
  useChartBase,
} from '@/components/ui/chart'
import type { MerchantStat } from '@/modules/projects/stores/types'
import {
  cn,
  numberToCompactCurrency,
  numberToCurrency,
  semanticColors,
  truncate,
} from '@/utils'

const DEFAULT_TOP_N = 15
const MARGIN = { top: 8, right: 56, bottom: 28, left: 180 }

interface TopMerchantsChartProps {
  merchants: MerchantStat[]
  topN?: number
  onMerchantClick?: (merchantGroup: string) => void
  onChartReady?: (params: ChartControlsState) => void
}

export function TopMerchantsChart({
  merchants,
  topN = DEFAULT_TOP_N,
  onMerchantClick,
  onChartReady,
}: TopMerchantsChartProps) {
  const {
    containerRef,
    dimensions,
    isFullscreen,
    toggleFullscreen,
    hoveredData,
    setHoveredData,
  } = useChartBase(onChartReady)

  const handleMerchantClick = async (merchantGroup: string) => {
    if (isFullscreen) await toggleFullscreen()
    onMerchantClick?.(merchantGroup)
  }

  const topMerchants = useMemo(() => {
    return [...merchants]
      .sort((first, second) => Math.abs(second.amount) - Math.abs(first.amount))
      .slice(0, topN)
  }, [merchants, topN])

  const { xScale, yScale } = useMemo(() => {
    if (!dimensions.width || !dimensions.height || topMerchants.length === 0)
      return {}
    const max =
      d3.max(topMerchants, (merchant) => Math.abs(merchant.amount)) ?? 0
    const x = d3
      .scaleLinear()
      .domain([0, max])
      .nice()
      .range([MARGIN.left, dimensions.width - MARGIN.right])
    const y = d3
      .scaleBand<string>()
      .domain(topMerchants.map((merchant) => merchant.merchantGroup))
      .range([MARGIN.top, dimensions.height - MARGIN.bottom])
      .padding(0.2)
    return { xScale: x, yScale: y }
  }, [dimensions.width, dimensions.height, topMerchants])

  if (topMerchants.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground text-sm">
        No merchant data.
      </div>
    )
  }

  const xTicks = xScale?.ticks(5) ?? []
  const hovered = hoveredData as MerchantStat | null

  return (
    <div
      ref={containerRef}
      className={cn(
        'group relative h-full w-full',
        isFullscreen && 'bg-white p-6',
      )}
    >
      {dimensions.width > 0 && dimensions.height > 0 && xScale && yScale && (
        <svg
          role="img"
          aria-label="Top merchants by absolute spend"
          width={dimensions.width}
          height={dimensions.height}
          className="overflow-visible"
        >
          <g>
            {xTicks.map((tick) => {
              const tickX = xScale(tick)
              return (
                <g key={tick}>
                  <line
                    x1={tickX}
                    x2={tickX}
                    y1={MARGIN.top}
                    y2={dimensions.height - MARGIN.bottom}
                    stroke="currentColor"
                    strokeOpacity={0.08}
                  />
                  <text
                    x={tickX}
                    y={dimensions.height - MARGIN.bottom + 16}
                    textAnchor="middle"
                    className="fill-muted-foreground text-[10px]"
                  >
                    {numberToCompactCurrency(tick)}
                  </text>
                </g>
              )
            })}
          </g>

          <g>
            {topMerchants.map((merchant) => {
              const bandY = yScale(merchant.merchantGroup) ?? 0
              const bandHeight = yScale.bandwidth()
              const barX = xScale(0)
              const barWidth = Math.max(
                xScale(Math.abs(merchant.amount)) - barX,
                1,
              )
              const barColor =
                merchant.amount >= 0
                  ? semanticColors.inflow
                  : semanticColors.outflow
              return (
                <g
                  key={merchant.merchantGroup}
                  className="cursor-pointer"
                  onPointerEnter={() => setHoveredData(merchant)}
                  onPointerLeave={() => setHoveredData(null)}
                  onClick={() => handleMerchantClick(merchant.merchantGroup)}
                >
                  <text
                    x={MARGIN.left - 8}
                    y={bandY + bandHeight / 2}
                    textAnchor="end"
                    dominantBaseline="middle"
                    className="fill-foreground text-[11px] capitalize"
                  >
                    {truncate(merchant.merchantGroup, 24)}
                  </text>
                  <rect
                    x={barX}
                    y={bandY}
                    width={barWidth}
                    height={bandHeight}
                    rx={3}
                    fill={barColor}
                    className="opacity-85 transition-opacity hover:opacity-100"
                  />
                  <text
                    x={barX + barWidth + 6}
                    y={bandY + bandHeight / 2}
                    dominantBaseline="middle"
                    className="fill-muted-foreground text-[10px] tabular-nums"
                  >
                    {numberToCompactCurrency(merchant.amount)}
                  </text>
                </g>
              )
            })}
          </g>
        </svg>
      )}

      <ChartTooltip
        active={!!hovered}
        position={
          hovered && xScale && yScale
            ? {
                x: xScale(Math.abs(hovered.amount)) / 2 + MARGIN.left / 2,
                y:
                  (yScale(hovered.merchantGroup) ?? 0) + yScale.bandwidth() / 2,
              }
            : undefined
        }
      >
        {hovered && (
          <>
            <div className="font-semibold capitalize">
              {hovered.merchantGroup}
            </div>
            <div className="text-muted-foreground opacity-80">
              {numberToCurrency(hovered.amount)} · {hovered.count} txns
            </div>
          </>
        )}
      </ChartTooltip>
    </div>
  )
}
